import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { fmtDateTime, fmtTime } from "@/lib/datetime";
import AnalyticsRangeFilter from "@/components/AnalyticsRangeFilter";
import { ArrowLeft, RefreshCw, ChevronDown, ChevronRight, Bot, User, HelpCircle, CheckCircle2, Trash2, ChevronLeft, ChevronsLeft, ChevronsRight, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import MediaInteractionThumbnail from "@/components/admin/MediaInteractionThumbnail";

/** Formatiert die vom Backend gelieferte anonymisierte IP (z. B.
 *  `93.184.216.0`) für die Admin-Anzeige um in `93.184.216.***`. Für
 *  IPv6 (`.../48`-Präfix) wird der Trailing-`::` durch `***` ersetzt.
 *  Backend speichert weiterhin die netzwerk-aggregierte Form für Rate-
 *  Limiting / Reports; das Frontend zeigt nur die maskierte Darstellung. */
function formatAnonIp(ip: string): string {
    const s = (ip || "").trim();
    if (!s) return "";
    if (s.includes(":")) {
        // IPv6 → letzte Gruppe durch *** ersetzen
        return s.replace(/(:0)+$/i, "").replace(/::$/, "") + ":***";
    }
    const parts = s.split(".");
    if (parts.length === 4) return `${parts[0]}.${parts[1]}.${parts[2]}.***`;
    return "***";
}

type Session = {
    session_id_hash: string;
    first_seen: string;
    last_seen: string;
    duration_seconds: number;
    event_count: number;
    page_count: number;
    pages_visited: string[];
    device_type: string;
    country: string | null;
    referrer_host: string | null;
    referrer_category: string;
    resolved_source?: string | null;
    source_label?: string | null;
    remote_addr_anon?: string | null;
    vt_code: string | null;
    vt_number: number | null;
    vt_name: string | null;
    converted: boolean;
    viewer_events_count?: number;
    viewer_play_count?: number;
    human_score: number;
    human_label: "likely_human" | "unclear" | "likely_bot";
};

type Payload = {
    range: string;
    sessions: Session[];
    total_count: number;
    total?: number;
    page?: number;
    page_size?: number;
    total_pages?: number;
    human_estimate: number;
    bot_estimate: number;
    unclear_estimate: number;
};

type EventRow = {
    timestamp: string;
    event_type: string;
    page_path?: string;
    referrer_host?: string | null;
    referrer_category?: string | null;
    duration_ms?: number;
    vt_code?: string | null;
    custom_data?: Record<string, unknown> | null;
};

const RANGES: Array<{ key: string; label: string }> = [
    { key: "today", label: "Heute" },
    { key: "yesterday", label: "Gestern" },
    { key: "7d", label: "7 Tage" },
    { key: "30d", label: "30 Tage" },
];
void RANGES; // Legacy — der neue AnalyticsRangeFilter kennt die Presets selbst.

/** Erlaubte Sortier-Schlüssel (identisch zum Server). Server fällt auf
 *  `time` zurück, wenn ein unbekannter Wert kommt. */
type SortKey =
    | "time" | "country" | "device" | "source" | "pages"
    | "duration" | "events" | "agent" | "conversion" | "score";

type SortOrder = "asc" | "desc";

function scoreStyle(label: Session["human_label"]): React.CSSProperties {
    if (label === "likely_human") return { background: "hsl(140 40% 82%)", color: "hsl(140 60% 22%)" };
    if (label === "likely_bot") return { background: "hsl(0 45% 88%)", color: "hsl(0 60% 30%)" };
    return { background: "hsl(45 55% 85%)", color: "hsl(35 55% 30%)" };
}

function scoreIcon(label: Session["human_label"]) {
    if (label === "likely_human") return <User size={12} />;
    if (label === "likely_bot") return <Bot size={12} />;
    return <HelpCircle size={12} />;
}

function scoreLabel(label: Session["human_label"]): string {
    if (label === "likely_human") return "Mensch";
    if (label === "likely_bot") return "Bot";
    return "Unklar";
}

function flag(cc: string | null | undefined): string {
    if (!cc || cc.length !== 2) return "";
    const A = 127397;
    return String.fromCodePoint(cc.toUpperCase().charCodeAt(0) + A) +
           String.fromCodePoint(cc.toUpperCase().charCodeAt(1) + A);
}

export default function AdminAnalyticsSessionsPage() {
    const [range, setRange] = useState("today");
    const [onlyHumans, setOnlyHumans] = useState(true);
    const [data, setData] = useState<Payload | null>(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<string | null>(null);
    /** Aktuelle Seite (1-indiziert). Wird bei Range- oder Größenänderung
     *  auf 1 zurückgesetzt. */
    const [page, setPage] = useState(1);
    /** Sessions pro Seite. Server clampt auf 1..100. */
    const [pageSize, setPageSize] = useState(50);
    /** Aktuelle Sortier-Spalte (Server-Key). Default `time`. */
    const [sortBy, setSortBy] = useState<SortKey>("time");
    /** Sortier-Richtung (asc | desc). Default `desc`. */
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        const res = await api<Payload>(
            `/api/admin/analytics/sessions?range=${range}&page=${page}&page_size=${pageSize}` +
            `&sort_by=${sortBy}&sort_order=${sortOrder}`,
        );
        if (res.ok && res.data) {
            setData(res.data);
            // Server clampt page, wenn > total_pages (z. B. nach Löschung
            // auf letzter Seite). UI zieht nach.
            if (typeof res.data.page === "number" && res.data.page !== page) {
                setPage(res.data.page);
            }
        }
        else setError(res.error || "Sessions konnten nicht geladen werden.");
        setLoading(false);
    }, [range, page, pageSize, sortBy, sortOrder]);

    // Range- oder Seitengrößen-Wechsel → zurück auf Seite 1.
    useEffect(() => { setPage(1); }, [range, pageSize]);

    /** Handler beim Klick auf eine Spaltenüberschrift.
     *  - Klick auf inaktive Spalte → aktiv, `desc`
     *  - Klick auf aktive Spalte (desc) → toggelt zu `asc`
     *  - Klick auf aktive Spalte (asc)  → toggelt zurück zu `desc`
     *  Setzt `page=1`, damit das erste Ergebnis der neuen Sortierung sichtbar ist. */
    const changeSort = useCallback((next: SortKey) => {
        if (next === sortBy) {
            setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
        } else {
            setSortBy(next);
            setSortOrder("desc");
        }
        setPage(1);
    }, [sortBy]);

    /** Löscht eine einzelne Session inkl. aller Events. Bestätigung via
     *  natives `confirm()`. Bei Erfolg: Liste neu laden + Toast. */
    const deleteSession = useCallback(async (sessionHash: string) => {
        const ok = window.confirm(
            "Diese Session inkl. aller Events wirklich löschen? Dies kann nicht rückgängig gemacht werden.",
        );
        if (!ok) return;
        const res = await api<{ deleted_count: number }>(
            `/api/admin/analytics/sessions/${sessionHash}`,
            { method: "DELETE" },
        );
        if (res.ok && res.data) {
            toast.success(`Session gelöscht (${res.data.deleted_count} Events entfernt).`);
            if (expanded === sessionHash) setExpanded(null);
            void load();
        } else {
            toast.error(res.error || "Session konnte nicht gelöscht werden.");
        }
    }, [expanded, load]);

    /** Bulk-Löschung aller Sessions, deren letzter Timestamp älter als 24 h
     *  ist. Nur mit Bestätigung. */
    const deleteOlderThan24h = useCallback(async () => {
        const ok = window.confirm(
            "Alle Sessions älter als 24 h inklusive Events wirklich löschen? Dies kann nicht rückgängig gemacht werden.",
        );
        if (!ok) return;
        const res = await api<{ deleted_count: number; sessions_removed: number }>(
            `/api/admin/analytics/sessions?older_than_hours=24`,
            { method: "DELETE" },
        );
        if (res.ok && res.data) {
            toast.success(
                `${res.data.sessions_removed} Session${res.data.sessions_removed === 1 ? "" : "s"} gelöscht ` +
                `(${res.data.deleted_count} Events entfernt).`,
            );
            void load();
        } else {
            toast.error(res.error || "Bulk-Löschung fehlgeschlagen.");
        }
    }, [load]);

    useEffect(() => { void load(); }, [load]);

    const filtered = data?.sessions.filter((s) => !onlyHumans || s.human_label !== "likely_bot") || [];

    return (
        <div data-testid="admin-analytics-sessions">
            <Link
                to="/admin/analytics"
                className="inline-flex items-center gap-1 text-sm text-hf-muted hover:text-hf-ink transition-colors"
            >
                <ArrowLeft size={14} /> Zurück zur Analytics-Übersicht
            </Link>

            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Analytics · Sessions</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">Session-Detail</h1>
                    <p className="mt-1 text-hf-muted">
                        Herkunft, Verweildauer und Human-Score pro Session — hilft, echte Menschen von Bots zu unterscheiden.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <AnalyticsRangeFilter
                        value={range}
                        onChange={setRange}
                        testIdPrefix="analytics-sessions-range"
                    />
                    <label className="inline-flex items-center gap-2 text-xs text-hf-ink cursor-pointer">
                        <input
                            type="checkbox"
                            checked={onlyHumans}
                            onChange={(e) => setOnlyHumans(e.target.checked)}
                            className="accent-[hsl(var(--hf-gold-dark))]"
                            data-testid="analytics-sessions-only-humans"
                        />
                        Nur Menschen zeigen
                    </label>
                    <button
                        type="button"
                        onClick={() => void load()}
                        className="hf-btn hf-btn-ghost"
                        data-testid="analytics-sessions-refresh"
                    >
                        <RefreshCw size={13} /> Aktualisieren
                    </button>
                    <button
                        type="button"
                        onClick={() => void deleteOlderThan24h()}
                        className="hf-btn hf-btn-ghost"
                        style={{ color: "hsl(var(--hf-danger))" }}
                        data-testid="analytics-sessions-bulk-delete-24h"
                        title="Alle Sessions inkl. Events löschen, die älter als 24 Stunden sind."
                    >
                        <Trash2 size={13} /> Ältere als 24 h löschen
                    </button>
                </div>
            </div>

            {data && (
                <div
                    className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3"
                    data-testid="analytics-sessions-summary"
                >
                    <Stat label="Sessions gesamt" value={data.total_count} />
                    <Stat label="Menschen (heuristisch)" value={data.human_estimate} tone="ok" />
                    <Stat label="Unklar" value={data.unclear_estimate} tone="warn" />
                    <Stat label="Bots" value={data.bot_estimate} tone="bot" />
                </div>
            )}

            {error && (
                <div
                    className="mt-6 p-3 rounded text-sm"
                    style={{
                        background: "hsl(var(--hf-danger) / 0.12)",
                        color: "hsl(var(--hf-danger))",
                        border: "1px solid hsl(var(--hf-danger) / 0.3)",
                    }}
                >
                    {error}
                </div>
            )}

            {loading ? (
                <div className="mt-10 text-hf-muted text-sm">Wird geladen…</div>
            ) : filtered.length === 0 ? (
                <div
                    className="mt-10 p-12 text-center rounded-sm"
                    style={{ background: "hsl(var(--hf-bg))", border: "1px dashed hsl(var(--hf-line))" }}
                >
                    <div className="text-hf-muted">
                        {onlyHumans && (data?.total_count || 0) > 0
                            ? "Keine menschlichen Sessions in diesem Zeitraum — nur Bots/unklar."
                            : "Keine Sessions in diesem Zeitraum."}
                    </div>
                </div>
            ) : (
                <div
                    className="mt-8 rounded-sm overflow-hidden"
                    style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                >
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="analytics-sessions-table">
                            <thead>
                                <tr
                                    className="text-left uppercase tracking-[0.14em] text-[10px] text-hf-muted"
                                    style={{ background: "hsl(var(--hf-surface))" }}
                                >
                                    <th className="px-3 py-3 w-6"></th>
                                    <SortableHeader label="Zeit"      sortKey="time"       sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
                                    <SortableHeader label="Land"      sortKey="country"    sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
                                    <SortableHeader label="Gerät"     sortKey="device"     sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
                                    <SortableHeader label="Herkunft"  sortKey="source"     sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
                                    <SortableHeader label="Seiten"    sortKey="pages"      sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
                                    <SortableHeader label="Dauer"     sortKey="duration"   sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} align="right" />
                                    <SortableHeader label="Events"    sortKey="events"     sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} align="right" />
                                    <SortableHeader label="Vertreter" sortKey="agent"      sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
                                    <SortableHeader label="Konv."     sortKey="conversion" sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} align="center" />
                                    <SortableHeader label="Score"     sortKey="score"      sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
                                    <th className="px-3 py-3 text-center w-8" aria-label="Aktion"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((s) => (
                                    <SessionRow
                                        key={s.session_id_hash}
                                        s={s}
                                        expanded={expanded === s.session_id_hash}
                                        onToggle={() => setExpanded(expanded === s.session_id_hash ? null : s.session_id_hash)}
                                        onDelete={() => void deleteSession(s.session_id_hash)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {data && (
                        <PaginationBar
                            page={data.page ?? page}
                            pageSize={data.page_size ?? pageSize}
                            totalPages={data.total_pages ?? 1}
                            total={data.total ?? data.total_count}
                            onPage={(p) => setPage(p)}
                            onPageSize={(ps) => setPageSize(ps)}
                        />
                    )}
                </div>
            )}

            <div className="mt-6 text-xs text-hf-muted">
                Historische Events aus der Zeit vor Bot-Filter-Aktivierung können in
                den letzten 7 Tagen noch Bot-Traffic enthalten — der TTL-Index
                löscht sie nach 90 Tagen automatisch.
            </div>
        </div>
    );
}

function SessionRow({
    s, expanded, onToggle, onDelete,
}: {
    s: Session;
    expanded: boolean;
    onToggle: () => void;
    onDelete: () => void;
}) {
    const [events, setEvents] = useState<EventRow[] | null>(null);
    const [loadingEvents, setLoadingEvents] = useState(false);

    useEffect(() => {
        if (!expanded || events !== null) return;
        setLoadingEvents(true);
        void (async () => {
            const res = await api<{ events: EventRow[] }>(`/api/admin/analytics/sessions/${s.session_id_hash}/events`);
            setLoadingEvents(false);
            if (res.ok && res.data) setEvents(res.data.events);
        })();
    }, [expanded, events, s.session_id_hash]);

    return (
        <>
            <tr
                className="border-t hover:bg-hf-surface transition-colors cursor-pointer"
                style={{ borderColor: "hsl(var(--hf-line))" }}
                onClick={onToggle}
                data-testid={`analytics-session-row-${s.session_id_hash.slice(0, 8)}`}
            >
                <td className="px-3 py-3">
                    {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-hf-muted">
                    {(() => {
                        const first = new Date(s.first_seen).getTime();
                        const last = new Date(s.last_seen).getTime();
                        const span = last - first;
                        const isLong = span > 6 * 3600 * 1000;
                        if (!isLong) {
                            return fmtDateTime(s.first_seen, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                        }
                        return (
                            <div className="leading-tight">
                                <div className="text-hf-ink">
                                    <span className="text-[9px] uppercase tracking-wider text-hf-muted mr-1">Zuletzt</span>
                                    {fmtDateTime(s.last_seen, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                </div>
                                <div className="text-[10px] text-hf-muted">
                                    <span className="text-[9px] uppercase tracking-wider mr-1">Start</span>
                                    {fmtDateTime(s.first_seen, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                </div>
                            </div>
                        );
                    })()}
                </td>
                <td className="px-3 py-3">
                    {s.country ? (
                        <span>{flag(s.country)} <span className="text-hf-muted">{s.country}</span></span>
                    ) : (
                        <span className="text-hf-muted">—</span>
                    )}
                </td>
                <td className="px-3 py-3 text-hf-muted capitalize">{s.device_type}</td>
                <td className="px-3 py-3 max-w-[220px]">
                    <div className="text-hf-ink truncate" title={s.referrer_host || undefined}>
                        {s.source_label || s.referrer_host || <span className="text-hf-muted">direct</span>}
                    </div>
                    <div className="text-[10px] text-hf-muted uppercase">
                        {s.resolved_source || s.referrer_category}
                    </div>
                    {s.remote_addr_anon && (
                        <div
                            className="text-[10px] text-hf-muted mt-0.5 font-mono"
                            title="Anonymisierte IP · letztes Oktett maskiert (DSGVO-konform)"
                            data-testid="session-ip-anon"
                        >
                            IP: {formatAnonIp(s.remote_addr_anon)}
                        </div>
                    )}
                </td>
                <td className="px-3 py-3" style={{ maxWidth: 320, width: 320 }}>
                    <div className="flex flex-col gap-1 min-w-0">
                        {s.pages_visited.slice(0, 3).map((p) => {
                            const [base, query] = splitPath(p);
                            return (
                                <span
                                    key={p}
                                    className="text-[10px] rounded-sm font-mono block max-w-full"
                                    style={{ background: "hsl(var(--hf-surface-2))", color: "hsl(var(--hf-ink))" }}
                                    title={p}
                                >
                                    <span className="inline-block px-1.5 py-0.5 max-w-full overflow-hidden text-ellipsis whitespace-nowrap align-middle">
                                        <strong className="font-semibold">{base}</strong>
                                        {query && (
                                            <span className="text-hf-muted ml-1 font-normal">
                                                {truncateQuery(query, 60)}
                                            </span>
                                        )}
                                    </span>
                                </span>
                            );
                        })}
                        {s.pages_visited.length > 3 && (
                            <span className="text-[10px] text-hf-muted">+{s.pages_visited.length - 3} weitere</span>
                        )}
                    </div>
                </td>
                <td className="px-3 py-3 text-right text-hf-muted whitespace-nowrap" style={{ minWidth: 64 }}>
                    {s.duration_seconds}s
                </td>
                <td className="px-3 py-3 text-right text-hf-muted whitespace-nowrap" style={{ minWidth: 56 }}>{s.event_count}</td>
                <td className="px-3 py-3 whitespace-nowrap" style={{ minWidth: 120 }}>
                    {s.vt_number != null ? (
                        <span className="text-xs">
                            Nr. {s.vt_number}
                            {s.vt_name && <span className="text-hf-muted"> · {s.vt_name}</span>}
                        </span>
                    ) : (
                        <span className="text-hf-muted">—</span>
                    )}
                </td>
                <td className="px-3 py-3 text-center">
                    {s.converted ? <CheckCircle2 size={16} className="inline text-hf-gold-dark" /> : <span className="text-hf-muted">—</span>}
                </td>
                <td className="px-3 py-3">
                    <span
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm"
                        style={scoreStyle(s.human_label)}
                        data-testid={`analytics-session-score-${s.session_id_hash.slice(0, 8)}`}
                    >
                        {scoreIcon(s.human_label)} {scoreLabel(s.human_label)} · {s.human_score}
                    </span>
                </td>
                <td className="px-3 py-3 text-center">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="p-1.5 rounded-sm transition-colors hover:bg-red-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
                        style={{ color: "hsl(var(--hf-danger))" }}
                        aria-label="Session löschen"
                        title="Session inkl. aller Events löschen"
                        data-testid={`analytics-session-delete-${s.session_id_hash.slice(0, 8)}`}
                    >
                        <Trash2 size={14} />
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr style={{ borderColor: "hsl(var(--hf-line))" }} className="border-t">
                    <td colSpan={12} className="p-4" style={{ background: "hsl(var(--hf-surface) / 0.4)" }}>
                        <div className="text-xs text-hf-muted mb-2">
                            Session-Hash: <span className="font-mono">{s.session_id_hash}</span> ·
                            von {fmtTime(s.first_seen)} bis {fmtTime(s.last_seen)} ·
                            besucht: {s.pages_visited.length} Seiten
                            {(s.viewer_events_count ?? 0) > 0 && (
                                <> · 360°-Interaktionen: <strong>{s.viewer_events_count}</strong>
                                {(s.viewer_play_count ?? 0) > 0 && <> (davon {s.viewer_play_count}× Play)</>}
                                </>
                            )}
                        </div>
                        {loadingEvents ? (
                            <div className="text-hf-muted text-sm">Events werden geladen…</div>
                        ) : events && events.length > 0 ? (
                            <ol className="text-xs space-y-1">
                                {events.map((e, i) => {
                                    const cd = e.custom_data as Record<string, unknown> | null | undefined;
                                    const mediaId =
                                        cd && typeof cd.media_id === "string" ? (cd.media_id as string) : undefined;
                                    const viewerType =
                                        cd && typeof cd.viewer_type === "string" ? (cd.viewer_type as string) : undefined;
                                    const mediaTitle =
                                        cd && typeof cd.media_title === "string" ? (cd.media_title as string) : undefined;
                                    const isInteraction = e.event_type === "interaction";
                                    // Pause-Erkennung: Zeitabstand zum vorherigen Event > 6h ⇒
                                    // grauen Zwischenstreifen rendern (Rückkehrer-Session).
                                    const prev = i > 0 ? events[i - 1] : null;
                                    const gapMs = prev
                                        ? new Date(e.timestamp).getTime() - new Date(prev.timestamp).getTime()
                                        : 0;
                                    const showPause = gapMs > 6 * 3600 * 1000;
                                    return (
                                        <Fragment key={i}>
                                            {showPause && <PauseSeparator gapMs={gapMs} />}
                                            <li className="flex items-start gap-2 py-0.5">
                                                {isInteraction ? (
                                                    <MediaInteractionThumbnail
                                                        mediaId={mediaId || ""}
                                                        viewerTypeHint={viewerType}
                                                        titleHint={mediaTitle}
                                                        size={40}
                                                    />
                                                ) : null}
                                                <div className="flex-1 min-w-0 flex flex-wrap items-start gap-2">
                                                    <span className="text-hf-muted whitespace-nowrap">
                                                        {fmtTime(e.timestamp)}
                                                    </span>
                                                    <span
                                                        className="inline-block text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider"
                                                        style={{
                                                            background: e.event_type === "conversion"
                                                                ? "hsl(140 40% 82%)"
                                                                : e.event_type === "interaction"
                                                                    ? "hsl(var(--hf-gold-soft) / 0.9)"
                                                                    : "hsl(var(--hf-surface-2))",
                                                            color: "hsl(var(--hf-ink))",
                                                        }}
                                                    >
                                                        {e.event_type}
                                                    </span>
                                                    <span className="font-mono text-hf-ink">{e.page_path || "—"}</span>
                                                    {typeof e.duration_ms === "number" && e.duration_ms > 0 && (
                                                        <span className="text-hf-muted">· {(e.duration_ms / 1000).toFixed(1)}s</span>
                                                    )}
                                                    {cd && Object.keys(cd).length > 0 && (
                                                        <span className="text-hf-muted truncate">
                                                            {JSON.stringify(cd).slice(0, 120)}
                                                        </span>
                                                    )}
                                                </div>
                                            </li>
                                        </Fragment>
                                    );
                                })}
                            </ol>
                        ) : (
                            <div className="text-hf-muted text-sm">Keine Events gefunden.</div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}

/** Zerlegt einen Pfad in (Basis, Query-String). */
function splitPath(path: string): [string, string | null] {
    const q = path.indexOf("?");
    if (q < 0) return [path, null];
    return [path.slice(0, q), path.slice(q)];
}

/** Kürzt einen Query-String auf `maxLen`. Fügt „ · +N Parameter" hinzu,
 *  wenn abgeschnitten wurde. */
function truncateQuery(q: string, maxLen: number): string {
    if (q.length <= maxLen) return q;
    // Parameter zählen
    const params = q.replace(/^\?/, "").split("&").filter(Boolean);
    const shown = q.slice(0, maxLen).replace(/[=&][^&=]*$/, "");
    // Ermitteln wie viele Parameter noch fehlen
    const shownCount = shown.replace(/^\?/, "").split("&").filter(Boolean).length;
    const rest = params.length - shownCount;
    return rest > 0 ? `${shown}… · +${rest} weitere` : `${shown}…`;
}

/** Zwischenstreifen für Pausen > 6h in Session-Detail. */
function PauseSeparator({ gapMs }: { gapMs: number }) {
    const label = formatPause(gapMs);
    return (
        <li
            className="flex items-center gap-3 py-2 my-1 select-none"
            style={{
                borderTop: "1px solid hsl(var(--hf-line))",
                borderBottom: "1px solid hsl(var(--hf-line))",
            }}
            aria-label={`Pause: ${label}`}
            data-testid="session-pause-separator"
        >
            <span
                aria-hidden
                className="flex-1"
                style={{ height: 1, background: "hsl(var(--hf-line))" }}
            />
            <span
                className="text-[10px] uppercase tracking-[0.16em] text-hf-muted whitespace-nowrap px-2"
                style={{ background: "hsl(var(--hf-surface) / 0.4)" }}
            >
                — {label} Pause —
            </span>
            <span
                aria-hidden
                className="flex-1"
                style={{ height: 1, background: "hsl(var(--hf-line))" }}
            />
        </li>
    );
}

function formatPause(ms: number): string {
    const totalMinutes = Math.floor(ms / 60000);
    const totalHours = Math.floor(totalMinutes / 60);
    if (totalHours >= 24) {
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        return hours > 0
            ? `${days} ${days === 1 ? "Tag" : "Tage"} ${hours} ${hours === 1 ? "Stunde" : "Stunden"}`
            : `${days} ${days === 1 ? "Tag" : "Tage"}`;
    }
    const hours = totalHours;
    const minutes = totalMinutes % 60;
    return minutes > 0
        ? `${hours} ${hours === 1 ? "Stunde" : "Stunden"} ${minutes} ${minutes === 1 ? "Minute" : "Minuten"}`
        : `${hours} ${hours === 1 ? "Stunde" : "Stunden"}`;
}

function Stat({
    label, value, tone,
}: {
    label: string;
    value: number;
    tone?: "ok" | "warn" | "bot";
}) {
    const bg =
        tone === "ok" ? "hsl(140 45% 90%)" :
        tone === "warn" ? "hsl(45 60% 90%)" :
        tone === "bot" ? "hsl(0 45% 92%)" :
        "hsl(var(--hf-bg))";
    return (
        <div
            className="p-4 rounded-sm"
            style={{ background: bg, border: "1px solid hsl(var(--hf-line))" }}
        >
            <div className="hf-eyebrow" style={{ fontSize: 10 }}>{label}</div>
            <div className="mt-2 font-display text-2xl text-hf-ink">{value}</div>
        </div>
    );
}

/**
 * Pagination-Leiste unter der Session-Tabelle.
 *  - Links:  „Zurück"-Button (Erste + eine Seite zurück) — deaktiviert auf Seite 1
 *  - Mitte:  „Seite X von Y · N Sessions gesamt" + kompakter Seitenspringer
 *  - Rechts: „Weiter"-Button + Seiten-Größen-Dropdown (25/50/100)
 *
 * Nutzt das Admin-Panel-Standardschema (`hf-btn`-Klassen + border/muted-Tokens),
 * damit die Optik zu den anderen Admin-Buttons passt.
 */
function PaginationBar({
    page, pageSize, totalPages, total, onPage, onPageSize,
}: {
    page: number;
    pageSize: number;
    totalPages: number;
    total: number;
    onPage: (p: number) => void;
    onPageSize: (ps: number) => void;
}) {
    const canPrev = page > 1;
    const canNext = page < totalPages;
    const pages = buildPageWindow(page, totalPages);
    return (
        <div
            className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 border-t"
            style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-surface) / 0.4)" }}
            data-testid="analytics-sessions-pagination"
        >
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    onClick={() => onPage(1)}
                    disabled={!canPrev}
                    className="hf-btn hf-btn-ghost"
                    aria-label="Erste Seite"
                    data-testid="analytics-sessions-page-first"
                >
                    <ChevronsLeft size={14} />
                </button>
                <button
                    type="button"
                    onClick={() => onPage(Math.max(1, page - 1))}
                    disabled={!canPrev}
                    className="hf-btn hf-btn-ghost"
                    data-testid="analytics-sessions-page-prev"
                >
                    <ChevronLeft size={14} /> Zurück
                </button>
            </div>

            <div
                className="flex items-center gap-2 text-xs text-hf-muted"
                data-testid="analytics-sessions-page-info"
            >
                <span>
                    Seite <strong className="text-hf-ink">{page}</strong> von{" "}
                    <strong className="text-hf-ink">{totalPages}</strong> ·{" "}
                    <span>{total} Sessions gesamt</span>
                </span>
                {pages.length > 1 && (
                    <span className="hidden md:inline-flex items-center gap-1 ml-2">
                        {pages.map((p, i) =>
                            p === "…" ? (
                                <span key={`gap-${i}`} className="px-1 text-hf-muted">…</span>
                            ) : (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => onPage(p)}
                                    aria-current={p === page ? "page" : undefined}
                                    className="px-2 py-1 rounded-sm text-[11px] transition-colors"
                                    style={p === page ? {
                                        background: "hsl(var(--hf-ink))",
                                        color: "hsl(var(--hf-bg))",
                                        fontWeight: 600,
                                    } : {
                                        background: "hsl(var(--hf-bg))",
                                        color: "hsl(var(--hf-ink))",
                                        border: "1px solid hsl(var(--hf-line))",
                                    }}
                                    data-testid={`analytics-sessions-page-btn-${p}`}
                                >
                                    {p}
                                </button>
                            ),
                        )}
                    </span>
                )}
            </div>

            <div className="flex items-center gap-2">
                <label className="text-xs text-hf-muted inline-flex items-center gap-1.5">
                    Pro Seite:
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSize(parseInt(e.target.value, 10))}
                        className="px-2 py-1 rounded-sm text-xs bg-white"
                        style={{ border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="analytics-sessions-page-size"
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </label>
                <button
                    type="button"
                    onClick={() => onPage(Math.min(totalPages, page + 1))}
                    disabled={!canNext}
                    className="hf-btn hf-btn-ghost"
                    data-testid="analytics-sessions-page-next"
                >
                    Weiter <ChevronRight size={14} />
                </button>
                <button
                    type="button"
                    onClick={() => onPage(totalPages)}
                    disabled={!canNext}
                    className="hf-btn hf-btn-ghost"
                    aria-label="Letzte Seite"
                    data-testid="analytics-sessions-page-last"
                >
                    <ChevronsRight size={14} />
                </button>
            </div>
        </div>
    );
}

/** Baut ein kompaktes Fenster von Seitenzahlen mit „…"-Auslassungen für
 *  große Bereiche. Beispiel bei totalPages=13, current=7:
 *  `[1, "…", 5, 6, 7, 8, 9, "…", 13]`. */
function buildPageWindow(current: number, total: number): (number | "…")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const out: (number | "…")[] = [];
    const add = (v: number | "…") => { if (out[out.length - 1] !== v) out.push(v); };
    add(1);
    if (current - 2 > 2) add("…");
    for (let p = Math.max(2, current - 2); p <= Math.min(total - 1, current + 2); p++) {
        add(p);
    }
    if (current + 2 < total - 1) add("…");
    add(total);
    return out;
}


/**
 * Klickbarer Spaltenkopf mit Sort-Indikator.
 *  - Neutral (nicht aktive Spalte): dezentes ↕-Icon in `hf-muted`
 *  - Aktiv aufsteigend:  ↑ in `hf-ink` (bestehende Akzent-Farbe)
 *  - Aktiv absteigend:   ↓ in `hf-ink`
 *
 * Zustand kommt vom Parent (`sortBy` + `sortOrder`), Klick löst `onSort`
 * aus, der die Toggle-Logik (desc → asc → desc) im Parent hält.
 * ARIA: `role="columnheader"` + `aria-sort` je nach Zustand.
 */
function SortableHeader({
    label, sortKey, sortBy, sortOrder, onSort, align,
}: {
    label: string;
    sortKey: SortKey;
    sortBy: SortKey;
    sortOrder: SortOrder;
    onSort: (next: SortKey) => void;
    align?: "left" | "right" | "center";
}) {
    const active = sortBy === sortKey;
    const ariaSort: "ascending" | "descending" | "none" = !active
        ? "none"
        : sortOrder === "asc" ? "ascending" : "descending";
    const alignCls = align === "right"
        ? "justify-end"
        : align === "center" ? "justify-center" : "justify-start";
    const alignThCls = align === "right"
        ? "text-right"
        : align === "center" ? "text-center" : "text-left";
    const testId = `analytics-sessions-sort-${sortKey}`;

    return (
        <th
            role="columnheader"
            aria-sort={ariaSort}
            className={`px-3 py-3 ${alignThCls}`}
        >
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSort(sortKey);
                    }
                }}
                className={`inline-flex items-center gap-1 w-full ${alignCls} uppercase tracking-[0.14em] text-[10px] transition-colors rounded-sm px-1 -mx-1 py-0.5 -my-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-hf-ink/30`}
                style={{
                    color: active ? "hsl(var(--hf-ink))" : "hsl(var(--hf-muted))",
                    fontWeight: active ? 600 : 500,
                    background: "transparent",
                }}
                onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "hsl(var(--hf-surface-2, var(--hf-bg)))";
                }}
                onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                }}
                data-testid={testId}
                aria-label={`Sortieren nach ${label}${active ? ` (aktuell ${sortOrder === "asc" ? "aufsteigend" : "absteigend"})` : ""}`}
            >
                <span>{label}</span>
                {active ? (
                    sortOrder === "asc"
                        ? <ArrowUp size={11} strokeWidth={2.4} />
                        : <ArrowDown size={11} strokeWidth={2.4} />
                ) : (
                    <ChevronsUpDown size={11} strokeWidth={2} style={{ opacity: 0.5 }} />
                )}
            </button>
        </th>
    );
}

